import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AArrowDown, AArrowUp, CheckCircle2, Download, FileText, GitFork, History, Pencil, Pill, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Markdown } from '@/components/Markdown';
import { SampleContentNotice } from '@/components/notices';
import { Alert, Badge, Button, ButtonLink, IconButton, QueryError, Spinner } from '@/components/ui';
import { useMedications } from '@/features/meds/api';
import { formatBytes, openAttachment, saveAttachmentOffline, savedAttachmentPaths } from '@/lib/attachments';
import { useAuth } from '@/lib/auth';
import { db, unwrap } from '@/lib/db';
import { errorMessage, formatDate } from '@/lib/utils';
import { usePrefs } from '@/stores/prefs';
import type { Protocol } from '@/types/db';
import { useAcknowledgeProtocol, useCategories, usePendingAcknowledgments, useProtocol, useProtocolHistory } from './api';
import { ProtocolScopeBadge } from './ProtocolsPage';

function Attachments({ protocol }: { protocol: Protocol }) {
  const saved = useQuery({ queryKey: ['attachments', 'saved'], queryFn: savedAttachmentPaths, gcTime: 0 });
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (path: string) => saveAttachmentOffline(protocol.attachments.find((a) => a.path === path)!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', 'saved'] }),
    onError: (e) => setError(errorMessage(e)),
  });
  if (protocol.attachments.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="label-caps mb-2 text-stone-500 dark:text-stone-400">Attachments</h2>
      {error && <Alert tone="danger" className="mb-2">{error}</Alert>}
      <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
        {protocol.attachments.map((a) => {
          const isSaved = saved.data?.has(a.path);
          return (
            <li key={a.path} className="flex flex-wrap items-center gap-2.5 py-2.5">
              <FileText className="size-4 text-stone-400" />
              <button type="button" className="min-w-0 flex-1 truncate text-left font-medium text-stone-950 underline-offset-[3px] hover:underline dark:text-white" onClick={() => openAttachment(a).catch((e) => setError(errorMessage(e)))}>
                {a.name}
              </button>
              <span className="font-mono text-xs text-stone-500">{formatBytes(a.size)}</span>
              {isSaved ? (
                <Badge tone="success">
                  <WifiOff className="size-3" /> Offline
                </Badge>
              ) : (
                <Button size="sm" variant="outline" icon={<Download className="size-4" />} loading={save.isPending && save.variables === a.path} onClick={() => save.mutate(a.path)}>
                  Save offline
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function ProtocolDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeOrgId, isEditor } = useAuth();
  const { protocolFontScale, setProtocolFontScale } = usePrefs();
  const { data: protocol, isPending, isError, error, refetch } = useProtocol(id);
  const history = useProtocolHistory(id);
  const categories = useCategories();
  const pending = usePendingAcknowledgments();
  const acknowledge = useAcknowledgeProtocol();
  const meds = useMedications();
  const [showHistory, setShowHistory] = useState(false);

  const adapt = useMutation({
    mutationFn: async () => unwrap(await db.rpc('adapt_library_protocol', { p_org: activeOrgId, p_protocol: id })) as string,
    onSuccess: (newId) => navigate(`/admin/${activeOrgId}/protocols/${newId}`),
  });

  if (isPending) return <Spinner />;
  if (isError || !protocol) return <QueryError error={error ?? new Error('Protocol not found')} retry={() => refetch()} />;

  const needsAck = pending.data?.some((p) => p.protocol_id === protocol.id);
  const category = categories.data?.find((c) => c.slug === protocol.category_slug);
  const relatedMeds = protocol.related_medications
    .map((slug) => meds.data?.medications.find((m) => m.slug === slug))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <article>
      <Link to="/protocols" className="mb-3 inline-flex text-sm text-stone-500 hover:text-stone-950 dark:text-stone-400 dark:hover:text-white">
        ← Protocols
      </Link>
      <header className="mb-6 border-b border-stone-300 pb-4 dark:border-stone-700">
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-sm text-stone-500">
          {protocol.code && <span className="font-mono text-xs font-medium text-stone-700 dark:text-stone-300">{protocol.code}</span>}
          {category && <span>· {category.name}</span>}
          <ProtocolScopeBadge protocol={protocol} />
          {protocol.population !== 'all' && <Badge tone="neutral">{protocol.population}</Badge>}
        </div>
        <div className="flex items-start gap-2">
          <h1 className="flex-1 text-[1.75rem] font-semibold leading-tight tracking-tight">{protocol.title}</h1>
          <IconButton label="Decrease text size" onClick={() => setProtocolFontScale(protocolFontScale - 0.1)}>
            <AArrowDown className="size-5" />
          </IconButton>
          <IconButton label="Increase text size" onClick={() => setProtocolFontScale(protocolFontScale + 0.1)}>
            <AArrowUp className="size-5" />
          </IconButton>
        </div>
        <p className="mt-1.5 font-mono text-xs text-stone-500">
          Version {protocol.version}
          {protocol.effective_date && ` · Effective ${formatDate(protocol.effective_date)}`}
          {protocol.source_name && ` · ${protocol.source_name}`}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {isEditor && protocol.org_id && protocol.org_id === activeOrgId && (
            <ButtonLink size="sm" variant="outline" to={`/admin/${activeOrgId}/protocols/${protocol.id}`} icon={<Pencil className="size-4" />}>
              Edit
            </ButtonLink>
          )}
          {isEditor && activeOrgId && protocol.org_id === null && (
            <Button size="sm" variant="outline" icon={<GitFork className="size-4" />} loading={adapt.isPending} onClick={() => adapt.mutate()}>
              Adapt for my agency
            </Button>
          )}
        </div>
        {adapt.isError && <Alert tone="danger" className="mt-2">{errorMessage(adapt.error)}</Alert>}
      </header>

      {needsAck && (
        <Alert
          tone="warning"
          title="This protocol was updated and requires your acknowledgment"
          className="mb-4"
          action={
            <Button size="sm" icon={<CheckCircle2 className="size-4" />} loading={acknowledge.isPending} onClick={() => acknowledge.mutate(protocol.id)}>
              I have reviewed it
            </Button>
          }
        >
          {history.data?.[0]?.change_summary}
        </Alert>
      )}
      {acknowledge.isSuccess && !needsAck && <Alert tone="success" className="mb-4">Acknowledgment recorded.</Alert>}
      {protocol.is_sample && <SampleContentNotice what="protocol" />}

      {protocol.summary && <p className="mt-4 max-w-3xl text-lg leading-relaxed text-stone-700 dark:text-stone-300">{protocol.summary}</p>}

      <Markdown className="mt-4 max-w-3xl" fontScale={protocolFontScale}>
        {protocol.body_md || '_No written content — see attachments._'}
      </Markdown>

      <Attachments protocol={protocol} />

      {relatedMeds.length > 0 && (
        <section className="mt-10">
          <h2 className="label-caps mb-2.5 text-stone-500 dark:text-stone-400">Medications in this protocol</h2>
          <div className="flex flex-wrap gap-2">
            {relatedMeds.map((m) => (
              <Link key={m.slug} to={`/meds/${m.slug}`} className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-2.5 py-1.5 text-sm font-medium transition-colors hover:border-stone-500 dark:border-stone-700 dark:bg-ink-900 dark:hover:border-stone-500">
                <Pill className="size-4 text-stone-400 dark:text-stone-500" /> {m.genericName}
              </Link>
            ))}
          </div>
        </section>
      )}

      {(history.data?.length ?? 0) > 0 && (
        <section className="mt-10 border-t border-stone-200 pt-4 dark:border-stone-800">
          <button type="button" className="flex w-full items-center gap-2 text-sm font-medium" onClick={() => setShowHistory(!showHistory)} aria-expanded={showHistory}>
            <History className="size-4 text-stone-400" /> Version history <span className="font-mono text-xs text-stone-500">({history.data!.length})</span>
          </button>
          {showHistory && (
            <ol className="mt-3 space-y-3 border-l-2 border-stone-200 pl-4 dark:border-stone-700">
              {history.data!.map((h) => (
                <li key={h.id}>
                  <div className="text-sm font-semibold">
                    Version {h.published_version} <span className="font-normal text-stone-500">· {formatDate(h.reviewed_at ?? h.updated_at)}</span>
                  </div>
                  {h.change_summary && <p className="text-sm text-stone-600 dark:text-stone-300">{h.change_summary}</p>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </article>
  );
}
